<template>
  <app-right-sidebar
    :width="640"
    :show-sidebar="getShowSidebar"
    :form-invalid="$v.$invalid"
    :disable-button="disableButton"
    :sidebar-title="getSidebarTitle"
    title-style="center"
    buttons-style="center"
    @hideSidebar="hideSidebar($event)"
    @saveData="saveData"
  >
    <form :class="$style.addAssetForm">
      <div :class="$style.formMain">
        <div :class="$forms.formRow">
          <div :class="[$forms.inputGroup, $forms.fullWidthGroup, $style.description]">
            <app-select-group
              label="Description*"
              :value="getAssetType"
              :items="getOtherAssetsTypes"
              v-model="$v.asset.otherAssetType.$model"
              @input="setAssetType"
            />

            <p v-if="$v.asset.otherAssetType.$error"
               :class="$forms.errorMessage">
              Asset Type is required
            </p>
          </div>
        </div>

        <div :class="$forms.formRow">
          <div :class="[$forms.inputGroup, $style.inputGroup]">
            <app-currency-input v-model="$v.asset.currentValue.$model"
                                :value="asset.currentValue"
                                label="Current Value*"/>
            <p v-if="$v.asset.currentValue.$error"
               :class="$forms.errorMessage">
              Current Value is invalid
            </p>
          </div>

          <div :class="[$forms.inputGroup, $style.inputGroup]">
            <app-select-group
              label="Year Purchased / Leased*"
              :value="getYearPurchased"
              :items="getYears"
              v-model="$v.asset.yearPurchased.$model"
              @input="setYearPurchased"
            />
            <p v-if="$v.asset.yearPurchased.$error"
               :class="$forms.errorMessage">
              Year Purchased is required
            </p>
          </div>
        </div>
      </div>
      <div>
        <div :class="$forms.formRow">
          <div :class="[$forms.inputGroup, $style.inputGroup]">
            <app-currency-input v-model="$v.asset.monthlyPayment.$model"
                                :value="asset.monthlyPayment"
                                label="Monthly Payment(s)"/>
            <p v-if="$v.asset.monthlyPayment.$error"
               :class="$forms.errorMessage">
              Monthly Payment is invalid
            </p>
          </div>

          <div :class="[$forms.inputGroup, $style.inputGroup]">
            <app-currency-input v-model="$v.asset.balanceOwed.$model"
                                :value="asset.balanceOwed"
                                label="Balance Owed"/>
            <p v-if="$v.asset.balanceOwed.$error"
               :class="$forms.errorMessage">
              Balance Owed is invalid
            </p>
          </div>
        </div>

        <div v-if="!typeIsWholeLifePolicy"
             :class="$style.finalPayment">
          <h6>Final Payment</h6>

          <div :class="$forms.formRow">
            <div :class="[$forms.inputGroup, $style.finalPaymentFields]">
              <app-select-group
                :label="getFinalPaymentMonthLabel"
                :value="getFinalPaymentMonth"
                :class="$style.month"
                :items="getFinalPaymentMonths"
                v-model="$v.asset.finalPaymentMonth.$model"
                @input="setFinalPaymentMonth"
              />

              <app-select-group
                :label="getFinalPaymentYearLabel"
                :value="getFinalPaymentYear"
                :class="$style.year"
                :items="getFinalPaymentYears"
                v-model="$v.asset.finalPaymentYear.$model"
                @input="setFinalPaymentYear"
              />
            </div>
          </div>
        </div>
      </div>

    </form>

    <div v-if="serverError">
      <p :class="$forms.errorMessage">
        {{ serverError }}
      </p>
    </div>
  </app-right-sidebar>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { mustBePositive, mustBeZeroOrPositive } from '@/utils/validation';
import { transformMonthToNumber, transformMonthToString } from '@/utils/transforms';
import { getPossibleYears, getFutureYears, getFutureMonths } from '@/utils';
import { MONTHS, OTHER_ASSET_TYPES } from '@/constants';

export default {
  props: {
    showSidebar: {
      type: Boolean,
      default: false,
    },
    sidebarType: {
      type: String,
      default: 'add',
    },
    editedItem: {
      type: Object,
      default: null,
    },
    editedItemId: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      serverError: '',
      asset: {
        otherAssetType: '',
        currentValue: 0,
        yearPurchased: '',
        monthlyPayment: 0,
        balanceOwed: 0,
        finalPaymentMonth: '',
        finalPaymentYear: '',
      },
      disableButton: false,
      initialAsset: null,
    };
  },
  methods: {
    ...mapActions([
      'postNewAsset',
      'editAsset',
    ]),
    hideSidebar(e) {
      this.clearFields();
      this.$emit('hideSidebar', e);
    },
    saveData() {
      if (this.lodash.isEqual(this.initialAsset, this.asset)) {
        this.hideSidebar();
        return;
      }

      /* Disable Save button in sidebar to prevent double clicking */
      this.disableButton = true;

      const newItem = {};

      Object.keys(this.asset).forEach((key) => {
        newItem[key] = this.asset[key];
      });
      newItem.yearPurchased = +this.asset.yearPurchased;
      newItem.finalPaymentYear = +this.asset.finalPaymentYear;
      newItem.otherAssetType = OTHER_ASSET_TYPES.find(type => type
        .textValue === this.asset.otherAssetType).code;
      newItem.finalPaymentMonth = transformMonthToNumber(this.asset.finalPaymentMonth);

      if (this.sidebarType === 'edit') {
        newItem.id = this.editedItemId;

        this.editAsset({
          editedItemId: newItem.id,
          newItem,
          userId: this.getUserId,
        })
          .then(() => {
            this.hideSidebar();
          })
          .catch((err) => {
            this.serverError = err.response.data.title;
            this.disableButton = false;
          });
      } else {
        this.postNewAsset({
          newItem,
          userId: this.getUserId,
        })
          .then(() => {
            this.hideSidebar();
          })
          .catch((err) => {
            this.serverError = err.response.data.title;
            this.disableButton = false;
          });
      }
    },
    clearFields() {
      Object.keys(this.asset).forEach((key) => {
        this.asset[key] = '';
      });
      this.asset.currentValue = 0;
      this.asset.monthlyPayment = 0;
      this.asset.balanceOwed = 0;
      this.$v.$reset();
      this.disableButton = false;
    },
    setFormFields() {
      Object.keys(this.asset).forEach((key) => {
        if (this.editedItem[key]) {
          this.asset[key] = this.editedItem[key];
        }
      });
      this.asset.yearPurchased = this.asset.yearPurchased.toString();
      if (this.editedItem.finalPaymentMonth) {
        this.asset.finalPaymentMonth = transformMonthToString(this.editedItem.finalPaymentMonth);
        this.asset.finalPaymentYear = this.asset.finalPaymentYear.toString();
      }

      this.asset.otherAssetType = OTHER_ASSET_TYPES.find(type => type
        .code === this.editedItem.otherAssetType).textValue;

      this.initialAsset = { ...this.asset };
    },
    resetServerErrors() {
      this.serverError = '';
    },
    /* Change the value of the select */
    setAssetType(val) {
      if (val === 'Whole Life Policy') {
        this.asset.finalPaymentMonth = '';
        this.asset.finalPaymentYear = '';
      }
      this.asset.assetType = val;
    },
    /* Change the value of the select */
    setFinalPaymentMonth(val) {
      this.asset.finalPaymentMonth = val;
    },
    /* Change the value of the select */
    setFinalPaymentYear(val) {
      this.asset.finalPaymentYear = val;
    },
    /* Change the value of the select */
    setYearPurchased(val) {
      this.asset.yearPurchased = val;
    },
  },
  computed: {
    ...mapGetters([
      'getUserId',
    ]),
    getMonths() {
      return MONTHS;
    },
    getOtherAssetsTypes() {
      return OTHER_ASSET_TYPES.map(type => type.textValue).sort();
    },
    getShowSidebar() {
      return this.showSidebar;
    },
    getSidebarTitle() {
      return this.sidebarType === 'edit' ? 'Edit Asset' : 'Add Asset';
    },
    /* Get vehicleType for displaying in table */
    getAssetType() {
      return this.asset.assetType;
    },
    /* Get finalPaymentMonth */
    getFinalPaymentMonth() {
      return this.asset.finalPaymentMonth;
    },
    /* Get finalPaymentYear */
    getFinalPaymentYear() {
      return this.asset.finalPaymentYear;
    },
    /* Get value from the select field */
    getYearPurchased() {
      return this.asset.yearPurchased;
    },
    /* Get possible years array for select */
    getYears() {
      return getPossibleYears();
    },
    getFinalPaymentYears() {
      return getFutureYears();
    },
    getFinalPaymentMonths() {
      const months = getFutureMonths(+this.getFinalPaymentYear);
      if (months.indexOf(this.getFinalPaymentMonth) < 0) {
        this.setFinalPaymentMonth('');
      }
      return months;
    },
    getFinalPaymentMonthLabel() {
      if (this.asset.balanceOwed > 0) {
        return 'Month*';
      }

      return 'Month';
    },
    getFinalPaymentYearLabel() {
      if (this.asset.balanceOwed > 0) {
        return 'Year*';
      }

      return 'Year';
    },
    typeIsWholeLifePolicy() {
      return this.asset.otherAssetType === 'Whole Life Policy';
    },
  },
  watch: {
    getShowSidebar() {
      if (this.showSidebar) {
        this.resetServerErrors();

        if (this.sidebarType === 'edit') {
          this.setFormFields();
        }
      }
    },
  },
  validations: {
    asset: {
      otherAssetType: {
        required,
      },
      currentValue: {
        required,
        mustBePositive,
      },
      yearPurchased: {
        required,
      },
      monthlyPayment: {
        mustBeZeroOrPositive,
      },
      balanceOwed: {
        mustBeZeroOrPositive,
      },
      finalPaymentYear: {
        required: requiredIf(vm => vm.balanceOwed > 0 && vm.otherAssetType !== 'Whole Life Policy'),
      },
      finalPaymentMonth: {
        required: requiredIf(vm => vm.balanceOwed > 0 && vm.otherAssetType !== 'Whole Life Policy'),
      },
    },
  },
};
</script>

<style src="@/styles/forms.scss" lang="scss" module="$forms"></style>
<style lang="scss" module>
  @import "@/styles/variables.scss";

  .addAssetForm {
    display: flex;
    flex-direction: column;
    margin-top: 20px;

    .description {
      margin-bottom: 12px;
    }

    .inputGroup {
      width: 268px;
      margin-right: 24px;

      &:last-child {
        margin-right: 0;
      }

      & > div {
        max-width: 100%;

        input {
          max-width: 268px;
        }
      }
    }

    .finalPayment {
      margin-top: 36px;

      h6 {
        margin-bottom: 16px;
      }

      &Fields {
        display: flex;

        .month {
          width: 144px;
          margin-right: 24px;
        }

        .year {
          width: 100px;
        }
      }
    }
  }
</style>
